import { supabase } from '../../../foundation/altzero_base/base/supabase/supabaseClient';
import html2canvas from 'html2canvas';
import { validateAdminAccess } from './adminService';

/**
 * Get user's organization ID
 */
const getUserOrganizationId = async (): Promise<string | null> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data: orgMember, error } = await supabase
      .from("base_organisation_members")
      .select("organisation_id")
      .eq("user_id", user.id)
      .limit(1)
      .single();

    if (error) {
      console.error("Error getting org ID:", error);
      return null;
    }

    return orgMember?.organisation_id || null;
  } catch (error) {
    console.error("Error getting organization ID:", error);
    return null;
  }
};

export interface Template {
  id: string;
  organisation_id: string;
  name: string;
  description?: string;
  file_path: string;
  thumbnail_url?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface TemplateData {
  name: string;
  description?: string;
  editorState: {
    pages: any[];
    currentPageId: string;
    zoom: number;
    showGrid: boolean;
  };
  exportInfo: {
    timestamp: string;
    version: string;
    totalPages: number;
    totalBlocks: number;
    exportType: string;
  };
  metadata: {
    userAgent: string;
    screenResolution: string;
    exportedBy: string;
  };
}

export interface SaveTemplateResult {
  success: boolean;
  template?: Template;
  error?: string;
}

export interface TemplateListResult {
  success: boolean;
  templates?: Template[];
  error?: string;
}

class TemplateStorageService {
  private readonly BUCKET_NAME = 'templates';
  private readonly THUMBNAILS_BUCKET = 'template-thumbnails';

  /**
   * Save template to Supabase storage and database
   */
  async saveTemplate(
    templateData: TemplateData,
    userId: string,
    templateId?: string
  ): Promise<SaveTemplateResult> {
    try {
      console.log('💾 Saving template:', templateData.name);

      // Generate file path
      const timestamp = Date.now();
      const sanitizedName = templateData.name.replace(/[^a-zA-Z0-9\-_\s]/g, '').replace(/\s+/g, '_');
      const fileName = `${sanitizedName}_${timestamp}.json`;
      const filePath = `${userId}/${fileName}`;

      // Upload template JSON to storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from(this.BUCKET_NAME)
        .upload(filePath, JSON.stringify(templateData, null, 2), {
          contentType: 'application/json',
          upsert: false,
        });

      if (uploadError) {
        console.error('❌ Template upload error:', uploadError);
        return { success: false, error: `Upload failed: ${uploadError.message}` };
      }

      console.log('✅ Template uploaded to storage:', uploadData.path);

      // Generate thumbnail (optional - will be generated later if needed)
      let thumbnailUrl: string | undefined;
      try {
        thumbnailUrl = await this.generateThumbnail(templateData);
      } catch (thumbnailError) {
        console.warn('⚠️ Thumbnail generation failed:', thumbnailError);
        // Continue without thumbnail - it's not critical
      }

      // Save or update template metadata in database
      let template: Template;
      
      // Get user's organization ID
      const orgId = await getUserOrganizationId();
      if (!orgId) {
        return { success: false, error: 'Organization not found. Please ensure you are a member of an organization.' };
      }

      if (templateId) {
        // Update existing template - only admin check, no created_by check
        const { data, error } = await supabase
          .from('templates')
          .update({
            name: templateData.name,
            description: templateData.description,
            file_path: uploadData.path,
            thumbnail_url: thumbnailUrl,
            updated_at: new Date().toISOString(),
          })
          .eq('id', templateId)
          .eq('organisation_id', orgId) // Ensure template belongs to organization
          .select()
          .single();

        if (error) {
          console.error('❌ Database update error:', error);
          return { success: false, error: `Database update failed: ${error.message}` };
        }

        template = data;
      } else {
        // Create new template
        const { data, error } = await supabase
          .from('templates')
          .insert({
            organisation_id: orgId,
            name: templateData.name,
            description: templateData.description,
            file_path: uploadData.path,
            thumbnail_url: thumbnailUrl,
            created_by: userId,
          })
          .select()
          .single();

        if (error) {
          console.error('❌ Database insert error:', error);
          return { success: false, error: `Database insert failed: ${error.message}` };
        }

        template = data;
      }

      console.log('✅ Template saved successfully:', template.id);
      return { success: true, template };

    } catch (error) {
      console.error('❌ Template save error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error occurred' 
      };
    }
  }

  /**
   * Update existing template - Admin only
   */
  async updateTemplate(templateId: string, templateData: TemplateData, userId: string): Promise<SaveTemplateResult> {
    try {
      // Validate admin access
      await validateAdminAccess(userId);
      // First, get the existing template to get the file path - no created_by check for admin
      const { data: existingTemplate, error: fetchError } = await supabase
        .from('templates')
        .select('*')
        .eq('id', templateId)
        .single();

      if (fetchError || !existingTemplate) {
        return {
          success: false,
          error: 'Template not found or access denied'
        };
      }

      // Update the file in storage
      const fileContent = JSON.stringify(templateData, null, 2);
      const { error: uploadError } = await supabase.storage
        .from('templates')
        .update(existingTemplate.file_path, fileContent, {
          contentType: 'application/json',
          upsert: true
        });

      if (uploadError) {
        return {
          success: false,
          error: `Failed to update template file: ${uploadError.message}`
        };
      }

      // Update template metadata in database - no created_by check for admin
      const { data: updatedTemplate, error: updateError } = await supabase
        .from('templates')
        .update({
          description: templateData.description,
          updated_at: new Date().toISOString()
        })
        .eq('id', templateId)
        .select()
        .single();

      if (updateError) {
        return {
          success: false,
          error: `Failed to update template metadata: ${updateError.message}`
        };
      }

      return {
        success: true,
        template: updatedTemplate
      };

    } catch (error) {
      console.error('Error updating template:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Get all templates for the user's organization (organization-level sharing)
   */
  async getUserTemplates(userId: string): Promise<TemplateListResult> {
    try {
      // Get user's organization ID
      const orgId = await getUserOrganizationId();
      if (!orgId) {
        console.warn('❌ No organization found for user:', userId);
        return { success: true, templates: [] };
      }

      // Fetch all templates from the user's organization
      const { data, error } = await supabase
        .from('templates')
        .select('*')
        .eq('organisation_id', orgId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error fetching organization templates:', error);
        return { success: false, error: error.message };
      }

      console.log(`✅ Fetched ${data?.length || 0} templates from organization ${orgId}`);
      return { success: true, templates: data || [] };
    } catch (error) {
      console.error('❌ Error fetching templates:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Get all templates for the user's organization (same as getUserTemplates for organization-level sharing)
   */
  async getAllTemplates(): Promise<TemplateListResult> {
    try {
      // Get user's organization ID
      const orgId = await getUserOrganizationId();
      if (!orgId) {
        console.warn('❌ No organization found for current user');
        return { success: true, templates: [] };
      }

      // Fetch all templates from the user's organization
      const { data, error } = await supabase
        .from('templates')
        .select('*')
        .eq('organisation_id', orgId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error fetching organization templates:', error);
        return { success: false, error: error.message };
      }

      console.log(`✅ Fetched ${data?.length || 0} templates from organization ${orgId}`);
      return { success: true, templates: data || [] };
    } catch (error) {
      console.error('❌ Error fetching all templates:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Load template data from storage
   */
  async loadTemplate(template: Template): Promise<{ success: boolean; data?: TemplateData; error?: string }> {
    try {
      const { data, error } = await supabase.storage
        .from(this.BUCKET_NAME)
        .download(template.file_path);

      if (error) {
        console.error('❌ Template download error:', error);
        return { success: false, error: error.message };
      }

      const text = await data.text();
      const templateData = JSON.parse(text) as TemplateData;

      return { success: true, data: templateData };
    } catch (error) {
      console.error('❌ Template load error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error occurred' 
      };
    }
  }

  /**
   * Delete template - Admin only
   */
  async deleteTemplate(templateId: string, userId: string): Promise<{ success: boolean; error?: string }> {
    try {
      // Validate admin access
      await validateAdminAccess(userId);
      // Get user's organization ID
      const orgId = await getUserOrganizationId();
      if (!orgId) {
        return { success: false, error: 'Organization not found. Please ensure you are a member of an organization.' };
      }

      // Get template info first - ensure it belongs to user's organization
      const { data: template, error: fetchError } = await supabase
        .from('templates')
        .select('file_path, thumbnail_url')
        .eq('id', templateId)
        .eq('organisation_id', orgId) // Ensure template belongs to organization
        .single();

      if (fetchError) {
        return { success: false, error: fetchError.message };
      }

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from(this.BUCKET_NAME)
        .remove([template.file_path]);

      if (storageError) {
        console.warn('⚠️ Storage deletion warning:', storageError);
        // Continue with database deletion even if storage fails
      }

      // Delete thumbnail if exists
      if (template.thumbnail_url) {
        try {
          const thumbnailPath = template.thumbnail_url.split('/').pop();
          if (thumbnailPath) {
            await supabase.storage
              .from(this.THUMBNAILS_BUCKET)
              .remove([`${userId}/${thumbnailPath}`]);
          }
        } catch (thumbnailError) {
          console.warn('⚠️ Thumbnail deletion warning:', thumbnailError);
        }
      }

      // Delete from database - no created_by check for admin
      const { error: dbError } = await supabase
        .from('templates')
        .delete()
        .eq('id', templateId);

      if (dbError) {
        return { success: false, error: dbError.message };
      }

      return { success: true };
    } catch (error) {
      console.error('❌ Template deletion error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error occurred' 
      };
    }
  }

  /**
   * Generate thumbnail for template (basic implementation)
   * This is a placeholder - in a real implementation, you might want to:
   * 1. Render the template in a hidden canvas
   * 2. Take a screenshot of the first page
   * 3. Upload the thumbnail to storage
   */
  private async generateThumbnail(templateData: TemplateData): Promise<string | undefined> {
    try {
      // For now, return undefined - thumbnails will be generated on-demand
      // In a future implementation, this could render the template and create a thumbnail
      return undefined;
    } catch (error) {
      console.warn('⚠️ Thumbnail generation failed:', error);
      return undefined;
    }
  }
}

export const templateStorageService = new TemplateStorageService();
